import { create } from 'zustand'
import { getSupabaseClient } from '@/repository/supabase/client'
import { fetchRoster, type HouseholdMember } from '@/repository/supabase/householdApi'

/**
 * v0.8.53 — Household roster cache for ownership tags.
 *
 * Keyed by workspace id rather than reset by `bindReposToSession`: the
 * facade only returns members when `loadedFor` matches the active
 * workspace, so a sign-out / household switch can never show a stale
 * roster. One fetch per workspace per session; the Household panel pushes
 * fresh rosters in via `setMembers` after renames / removals.
 *
 * Failure (signed-out, migration 004 missing, network) leaves an empty
 * roster for that workspace — ownership UI simply stays hidden.
 */
interface HouseholdState {
  members: HouseholdMember[]
  loadedFor: string | null
  inFlightFor: string | null
  load: (workspaceId: string) => Promise<void>
  setMembers: (workspaceId: string, members: HouseholdMember[]) => void
}

export const useHouseholdStore = create<HouseholdState>((set, get) => ({
  members: [],
  loadedFor: null,
  inFlightFor: null,

  load: async (workspaceId) => {
    const { loadedFor, inFlightFor } = get()
    if (loadedFor === workspaceId || inFlightFor === workspaceId) return
    const client = getSupabaseClient()
    if (!client) return
    set({ inFlightFor: workspaceId })
    let members: HouseholdMember[] = []
    try {
      members = await fetchRoster(client, workspaceId)
    } catch (err) {
      console.warn(
        '[DebtOS] household roster load failed:',
        err instanceof Error ? err.message : err
      )
    }
    // A newer load may have started for a different workspace meanwhile.
    if (get().inFlightFor !== workspaceId) return
    set({ members, loadedFor: workspaceId, inFlightFor: null })
  },

  setMembers: (workspaceId, members) => set({ members, loadedFor: workspaceId }),
}))
